import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { useFormikContext } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { SortableElement, SortableContainer } from 'react-sortable-hoc';
import { arrayMoveImmutable } from 'array-move';
import { selectStatusByName } from '@redux/selectors/FormSelector';
import { saveSupplement, reorderSupplements } from '@redux/modules/listing';
import { selectUploadedSupplements, selectSectionsPerRepeatableSection } from '@redux/selectors/ListingSelector';
import Swal from 'sweetalert2';
import SupplementCard from './SupplementCard';

const allowableFiles = '.doc, .docx, .pdf, .rtf, .txt, .xls, .xlsx, .gif, .jpg, .jpeg, .png, .tiff, .mov, .mp4, .mpeg, .wmv, .quicktime';

const MAX = 2;
const SortableCard = SortableElement(props => (
  <SupplementCard {...props} />
));

const Grid = SortableContainer(({ fileList, something, ...otherProps }) => (
  <div className={`grid grid-${MAX}-col`}>
    {fileList.map(([k, file], index) => (
      (!file.IsDeleted)
      && (
        <SortableCard
          key={k} // consumed by react-sortable-hoc
          index={index} // consumed by react-sortable-hoc
          sortIndex={index}
          file={file}
          {...otherProps}
        />
      )
    ))}
  </div>
));

const SupplementGrid = ({ fields, supplementsPayload }) => {
  const {
    values, setValues, setFieldValue,
  } = useFormikContext();
  const dispatch = useDispatch();
  const statusSupplement = useSelector(state => selectStatusByName(state, 'Supplements'));
  // const pageStructureHasRooms = useSelector(selectPageStructureHasRooms);

  const uploadList = useSelector(selectUploadedSupplements);
  // this property is duplicated in each member of fields
  const maxCount = useMemo(() => ((fields[0].repeatableMaxCount) ? fields[0].repeatableMaxCount : 25), [fields]);

  const sectionsPerRepeatableSection = useSelector(selectSectionsPerRepeatableSection);
  const supplementsCount = sectionsPerRepeatableSection.Supplements.length;

  const fileList = useMemo(() => {
    const array = [];
    if (values.Supplements) {
      Object.entries(values.Supplements).forEach(([k, file]) => {
        if (!file.IsDeleted) {
          array.push([k, file]);
        }
      });
    }
    return array;
  }, [values?.Supplements]);

  const displayMaxCountExceedMessage = () => Swal.fire({
    position: 'center',
    title: 'Supplements Exceeded',
    html: `The maximum number of supplements must not exceed <strong class="text-danger">${maxCount}</strong>.`,
    icon: 'error',
    iconColor: 'var(--theme-danger)',
    showConfirmButton: false,
    timer: 5000,
  });

  useEffect(() => {
    const formikSupplements = values.Supplements;
    uploadList.forEach(upload => {
      if (!upload.IsUploading) {
        // formik may not have the record.
        const uploadFormikRecord = formikSupplements?.[upload.uid] ?? null;
        if (uploadFormikRecord) {
          uploadFormikRecord.Description = upload.Description;
          uploadFormikRecord.Path = upload.Name ?? upload.URL;
          uploadFormikRecord.Name = upload.Name ?? upload.URL;
        }
      }
    });
  }, [uploadList]);

  const handleFileDrop = acceptedFiles => {
    if (supplementsCount + acceptedFiles.length > maxCount) {
      displayMaxCountExceedMessage();
      return;
    }

    // we batch supplements before adding to formik state, may need async hack per https://github.com/jaredpalmer/formik/issues/529
    const batch = {};
    acceptedFiles.map(file => {
      // generate UID to relate placeholder to POST response to formik state
      const id = uuidv4();
      // For each file, dispatch async thunk in Redux
      dispatch(saveSupplement(file, id));

      batch[id] = {
        uid: id,
        Description: '',
        SupplementType: '',
        SortOrder: uploadList.length + 1,
        IsDeleted: false,
      };
      return null;
    });
    // add uploads to formik state
    setValues({
      ...values,
      Supplements: {
        ...values.Supplements,
        ...batch,
      },
    });
  };

  useEffect(() => {
    if (supplementsPayload?.length) {
      const batch = {}; // adds object to formik
      const newUploadedSupp = []; // used for re ordering
      supplementsPayload.map(supp => {
        // generate UID to relate placeholder to POST response to formik state
        const id = uuidv4();
        batch[id] = {
          uid: id,
          Description: supp.displayName ?? supp.Description,
          SupplementType: supp.SupplementType,
          SortOrder: supp.SortOrder,
          IsDeleted: supp.IsDeleted,
          Name: supp.Path,
          Path: supp.Path,
          URL: supp.URL,
        };
        newUploadedSupp.push({
          uid: id,
          Path: supp.Path,
          Description: supp.displayName ?? supp.Description,
          UniqueID: supp.UniqueID,
          SupplementType: supp.SupplementType,
          MediaType: supp.MediaType,
          URL: supp.URL,
          SortOrder: supp.SortOrder,
          IsDeleted: supp.IsDeleted,
          IsLive: supp.IsLive,
          IsPreload: supp.IsPreload,
          Name: supp.Path,
        });
        return null;
      });
      // add uploads to formik state
      setFieldValue('Supplements', {
        ...values.Supplements,
        ...batch,
      });
      // add it to redux
      dispatch(reorderSupplements(newUploadedSupp));
    }
  }, [supplementsPayload]);

  // after sort ends derive new order, then dispatch results to redux
  const onSortEnd = ({ oldIndex, newIndex }) => {
    const newSortOrder = arrayMoveImmutable(uploadList, oldIndex, newIndex);
    dispatch(reorderSupplements(newSortOrder));

    const sortedArray = arrayMoveImmutable(fileList, oldIndex, newIndex);
    let newValues = {};
    sortedArray.forEach((file, index) => {
      newValues = {
        ...newValues,
        [file[0]]: {
          ...file[1],
          SortOrder: index + 1,
        },
      };
    });
    setValues({
      ...values,
      Supplements: { ...newValues },
    });
  };

  const { getRootProps, getInputProps } = useDropzone({ accept: allowableFiles, maxFiles: maxCount, onDrop: handleFileDrop });

  return (
    <div>
      <div {...getRootProps({ className: 'dropzoneContainer' })}>
        <input {...getInputProps()} />
        <p className="text-center mb-2">
          <i className="far fa-cloud-upload fa-2x" />
        </p>
        <p className="text-center text-muted mb-1">
          Drag &apos;n&apos; drop your supplement files here, or click to select
          them manually.
        </p>
        <p className="text-center fs-sm">
          Press &apos;Ctrl&apos; or ⌘ to multi-select
        </p>
      </div>
      {statusSupplement?.errorMessage && <div id="Supplements" className="invalid-feedback">{statusSupplement.errorMessage}</div>}
      <Grid
        axis="xy"
        distance={20} // set distance to prevent click events from being swallowed
        fileList={fileList}
        onSortEnd={onSortEnd}
        useDragHandle
      />
    </div>
  );
};

SupplementGrid.propTypes = {
  fields: PropTypes.arrayOf(PropTypes.shape()),
  supplementsPayload: PropTypes.arrayOf(PropTypes.shape()),
};

export default SupplementGrid;
